// SPDX-License-Identifier: GPL-3.0-or-later
// Helpers that the mesh generators share.

import { type BufferGeometry, ExtrudeGeometry, LatheGeometry, Shape, Vector2 } from "three";
import { CatmullCurve2D } from "../../engine/math/catmull-curve.ts";
import { DragPoint } from "../../engine/math/dragpoint.ts";
import { RenderVertex, type Vertex2D } from "../../engine/math/vertex2d.ts";
import { Vertex3D } from "../../engine/math/vertex3d.ts";
import type { DragPointSpec } from "../../shared/contracts.ts";

/** A flat point in table units. */
export interface Point2 {
  readonly x: number;
  readonly y: number;
}

/** Convert contract drag points to engine drag points. */
export function toDragPoints(points: readonly DragPointSpec[]): DragPoint[] {
  return points.map(
    (p) =>
      new DragPoint({
        vertex: new Vertex3D(p.vertex.x, p.vertex.y, p.vertex.z),
        fSmooth: p.fSmooth ?? false,
        fSlingshot: p.fSlingshot ?? false,
        fAutoTexture: p.fAutoTexture ?? true,
        texturecoord: p.texturecoord ?? 0,
      }),
  );
}

/** Smooth a closed outline of drag points. The result is a dense polyline. */
export function smoothLoop(points: readonly DragPointSpec[]): RenderVertex[] {
  return DragPoint.getRgVertex<RenderVertex>(
    toDragPoints(points),
    () => new RenderVertex(),
    (v0, v1, v2, v3) =>
      CatmullCurve2D.fromVertex2D(v0 as Vertex2D, v1 as Vertex2D, v2 as Vertex2D, v3 as Vertex2D),
    true,
    4.0,
  );
}

/**
 * Signed area of a closed outline (shoelace sum, half). With y down, a positive value means
 * that the outline runs clockwise on the screen.
 */
export function signedArea(points: readonly Point2[]): number {
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i] as Point2;
    const b = points[(i + 1) % points.length] as Point2;
    sum += a.x * b.y - b.x * a.y;
  }
  return sum / 2;
}

/**
 * Extrude a flat outline (table x, table y) upward by `depth`. The result is in the three.js
 * frame: the base lies at y = 0, the top at y = depth, and three z is the table y.
 * A positive `bevel` rounds the edge a little and adds `bevel` above and below.
 */
export function extrudeOutline(
  points: readonly Point2[],
  depth: number,
  bevel = 0,
): BufferGeometry {
  const shape = new Shape(points.map((p) => new Vector2(p.x, -p.y)));
  const geometry = new ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: bevel > 0,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelSegments: 1,
    steps: 1,
    curveSegments: 1,
  });
  geometry.rotateX(-Math.PI / 2);
  return geometry;
}

/** A circle as an outline with `segments` points. */
export function circleOutline(cx: number, cy: number, radius: number, segments = 24): Point2[] {
  const out: Point2[] = [];
  for (let i = 0; i < segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    out.push({ x: cx + Math.cos(a) * radius, y: cy + Math.sin(a) * radius });
  }
  return out;
}

/**
 * A solid of revolution about the vertical axis. Each profile entry is [radius, height].
 * List the entries from the bottom to the top. The normals point outward.
 */
export function lathe(
  profile: ReadonlyArray<readonly [number, number]>,
  segments = 28,
): LatheGeometry {
  return new LatheGeometry(
    profile.map(([r, y]) => new Vector2(r, y)),
    segments,
  );
}
